/* eslint-disable new-cap, @typescript-eslint/no-empty-function */
/**
 * REST API を使用した認証関連 hooks
 * OpenAPI仕様から生成された型を使用（SSOT）
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "../api/client";
import type { components } from "../api/schema";

// =====================================
// 生成された型を再エクスポート
// =====================================

export type User = components["schemas"]["User"];

// =====================================
// Python Backend 用の拡張型
// =====================================

/**
 * テナント情報
 * Python Backend (PostgreSQL) から返される
 */
export interface UserTenant {
  id: string;
  name: string;
  role: "owner" | "admin" | "member";
}

/**
 * 拡張ユーザー型
 * Python Backend: tenants が存在する
 * TS Backend: tenants が存在しない
 */
export type CurrentUser = User & {
  /** テナント一覧 (Python Backend のみ) */
  tenants?: UserTenant[];
};

// =====================================
// useCurrentUserRestApi - 現在のユーザー情報取得
// =====================================

export interface UseCurrentUserRestApiOptions {
  skip?: boolean;
  fetchPolicy?: "cache-first" | "network-only";
  errorPolicy?: "none" | "ignore" | "all";
  onCompleted?: () => void;
}

export interface UseCurrentUserRestApiResult {
  data: { me: User } | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<User>;
}

/**
 * 現在のユーザー情報を取得するhook
 * @param {UseCurrentUserRestApiOptions} options - オプション設定
 * @return {UseCurrentUserRestApiResult} ユーザー情報、エラー、ローディング状態、refetch関数
 */
export function useCurrentUserRestApi(
  options: UseCurrentUserRestApiOptions = {},
): UseCurrentUserRestApiResult {
  const [data, setData] = useState<{ me: User } | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(!options.skip);

  const fetchCurrentUser = useCallback(async (): Promise<User> => {
    setLoading(true);
    setError(undefined);

    try {
      const { data: responseData, error: apiError } =
        await api.GET("/api/auth/me");

      if (apiError) {
        throw new Error(
          (apiError as { message?: string }).message || "API error",
        );
      }

      if (!responseData) {
        throw new Error("No data received");
      }

      setData({ me: responseData });
      return responseData;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      if (options.errorPolicy !== "ignore") {
        setError(err);
      }
      throw err;
    } finally {
      setLoading(false);
      options.onCompleted?.();
    }
  }, [options.errorPolicy, options.onCompleted]);

  useEffect(() => {
    if (!options.skip) {
      fetchCurrentUser().catch(() => {}); // eslint-disable-line @typescript-eslint/no-empty-function
    }
  }, [options.skip, fetchCurrentUser]);

  return {
    data,
    error,
    loading,
    refetch: fetchCurrentUser,
  };
}
